import { Location } from '@angular/common';
import { HttpClient, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BackendAPI } from '@core/models/backend-api';
import { GalleryImage } from '@core/models/gallery-image';
import { Logger } from '@core/services/logger.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AppConfigService } from './app-config.service';

export class ImageFilteringOptions {
	minwidth?: number;
	maxwidth?: number;
	minheight?: number;
	maxheight?: number;
	elementKey?: string;
}

@Injectable({
	providedIn: 'root'
})
export class ImageGalleryService {

	private readonly COMPONENT_NAME = "ImageGalleryService";

	constructor(
		private logger: Logger,
		private http: HttpClient,
		private appConfig: AppConfigService) { }

	getGalleryImagesForPackageAndPlacement(packageId: string, placementId: string, imageFilteringOptions: ImageFilteringOptions | undefined = undefined): Observable<GalleryImage[]> {

		const url = BackendAPI.staticDataGalleryImagesForPackageAndPlacement;

		let requestParams = new HttpParams()
			.set("packageId", packageId)
			.set("placementId", placementId);

		let restrictedImages: string[];

		if (imageFilteringOptions) {
			if (imageFilteringOptions.minwidth) {
				requestParams = requestParams.set("minwidth", imageFilteringOptions.minwidth.toString());
			}

			if (imageFilteringOptions.maxwidth) {
				requestParams = requestParams.set("maxwidth", imageFilteringOptions.maxwidth.toString());
			}

			if (imageFilteringOptions.minheight) {
				requestParams = requestParams.set("minheight", imageFilteringOptions.minheight.toString());
			}

			if (imageFilteringOptions.maxheight) {
				requestParams = requestParams.set("maxheight", imageFilteringOptions.maxheight.toString());
			}

			if (imageFilteringOptions.elementKey) {
				const restrictedImageMap = this.appConfig.config?.restrictedGalleryImagesByElementKey || {};
				if (restrictedImageMap.hasOwnProperty(imageFilteringOptions.elementKey)) {
					restrictedImages = restrictedImageMap[imageFilteringOptions.elementKey];
				}
			}
		}

		return this.http.get<GalleryImage[]>(url, { params: requestParams }).pipe(
			map(images => {
				const result: GalleryImage[] = [];

				if (images && images.length > 0) {
					for (const img of images) {
						if (img) {
							if (!img.id) {
								this.logger.error(this.COMPONENT_NAME, `getGalleryImagesForPackageAndPlacement: a gallery image for packageId="${packageId}" placementId="${placementId}" contains empty id`);
								continue;
							}

							if (restrictedImages && img.label && !restrictedImages.includes(img.label)) {
								continue;
							}

							if (typeof img.id !== "string") {
								this.logger.warning(this.COMPONENT_NAME, "getGalleryImagesForPackageAndPlacement: the gallery image id is not a string.");
								img.id = "" + img.id; // convert to string
							}

							if (!img.label) {
								this.logger.warning(this.COMPONENT_NAME, `getGalleryImagesForPackageAndPlacement: the gallery image label is empty, id="${img.id}"`);
							}

							if (typeof img.label !== "string") {
								this.logger.warning(this.COMPONENT_NAME, `getGalleryImagesForPackageAndPlacement: the gallery image label is not a string, id="${img.id}"`);
								img.label = "" + img.label; // convert to string
							}

							result.push(img);
						}
					}
				}

				return result;
			}),
			catchError(error => {
				return throwError(error);
			})
		); // end of pipe;
	}

	createGalleryImageThumbnailURL(galleryImageId: string): string {
		const url = Location.joinWithSlash(Location.joinWithSlash(BackendAPI.staticDataGalleryImages, galleryImageId), "thumbnail");
		const request = new HttpRequest('GET', url, { responseType: 'blob' });
		return request.urlWithParams;
	}

	downloadHighresGalleryImage(galleryImageId: string): Observable<Blob> {
		const url = Location.joinWithSlash(Location.joinWithSlash(BackendAPI.staticDataGalleryImages, galleryImageId), "hires");
		return this.http.get(url, { responseType: 'blob' });
	}
}
